import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ChevronLeft, Sparkles, CheckCircle, Brain, Crown, Star } from 'lucide-react';
import { quizQuestions, membership } from '../data/plansData';

const PlanRecommender = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [recommendation, setRecommendation] = useState(null);

  const handleAnswer = (optionId, rec) => {
    setSelectedOption(optionId);
    
    setTimeout(() => {
      const newAnswers = [...answers];
      newAnswers[currentQuestion] = { optionId, recommendation: rec };
      setAnswers(newAnswers);

      if (currentQuestion < quizQuestions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption(null);
      } else {
        // Calculate: count "member" recommendations vs "free"
        const memberCount = newAnswers.filter(a => a.recommendation === 'member').length;
        const finalRec = memberCount >= 2 ? 'member' : 'free';
        setRecommendation(finalRec);
        setShowResults(true);
      }
    }, 400);
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers([]);
    setShowResults(false);
    setRecommendation(null);
    setSelectedOption(null);
  };

  const question = quizQuestions[currentQuestion];

  return (
    <section 
      id="quiz"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="plan-recommender"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-surface-dark via-surface to-surface-dark" />
      
      <motion.div
        className="absolute top-1/4 left-0 w-80 h-80 rounded-full bg-neon-green/10 blur-[100px]"
        animate={{ scale: [1, 1.3, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity }}
      />

      {[...Array(15)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1 h-1 bg-neon-green/40 rounded-full"
          style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%` }}
          animate={{ y: [0, -50, 0], opacity: [0.2, 0.6, 0.2] }}
          transition={{ duration: 4 + Math.random() * 3, repeat: Infinity, delay: Math.random() * 2 }}
        />
      ))}

      <div className="relative z-10 max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <motion.span 
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-neon-green/40 bg-neon-green/10 text-neon-green text-sm font-semibold mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
            >
              <Brain className="w-4 h-4" />
            </motion.div>
            Quiz Interativo
          </motion.span>
          <h2 className="font-outfit font-black text-3xl sm:text-4xl lg:text-5xl text-white mb-4">
            Você está pronto para{' '}
            <span className="gradient-text-green">construir junto?</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            Responda 3 perguntas rápidas e descubra o melhor caminho para você
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-surface/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden"
        >
          <motion.div
            className="absolute inset-0 rounded-3xl"
            style={{
              background: 'linear-gradient(90deg, transparent, rgba(0,255,0,0.3), transparent)',
              backgroundSize: '200% 100%',
            }}
            animate={{ backgroundPosition: ['200% 0', '-200% 0'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />

          <AnimatePresence mode="wait">
            {!showResults ? (
              <motion.div
                key={currentQuestion}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-center gap-3 mb-8">
                  {quizQuestions.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 flex-1 rounded-full overflow-hidden ${
                        index <= currentQuestion ? 'bg-neon-green/20' : 'bg-white/10'
                      }`}
                    >
                      <motion.div
                        className="h-full bg-neon-green rounded-full"
                        initial={{ width: 0 }}
                        animate={{ 
                          width: index < currentQuestion ? '100%' : 
                                 index === currentQuestion ? '50%' : '0%' 
                        }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                  ))}
                </div>

                <span className="text-neon-green text-sm font-mono mb-4 block">
                  Pergunta {currentQuestion + 1} de {quizQuestions.length}
                </span>

                <h3 className="font-outfit font-bold text-2xl sm:text-3xl text-white mb-8">
                  {question.question}
                </h3>

                <div className="grid gap-4">
                  {question.options.map((option, index) => (
                    <motion.button
                      key={option.id}
                      onClick={() => handleAnswer(option.id, option.recommendation)}
                      disabled={selectedOption !== null}
                      className={`w-full p-5 text-left rounded-xl border transition-all relative overflow-hidden ${
                        selectedOption === option.id
                          ? 'border-neon-green bg-neon-green/20'
                          : 'border-white/10 bg-surface-dark/50 hover:border-neon-green/50 hover:bg-surface-dark'
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ scale: selectedOption === null ? 1.02 : 1, x: 5 }}
                      whileTap={{ scale: 0.98 }}
                      data-testid={`quiz-option-${option.id}`}
                    >
                      <div className="flex items-center gap-4 relative z-10">
                        <span className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-mono transition-colors ${
                          selectedOption === option.id
                            ? 'bg-neon-green text-black'
                            : 'bg-neon-green/10 border border-neon-green/30 text-neon-green'
                        }`}>
                          {option.id.toUpperCase()}
                        </span>
                        <span className="text-white font-medium">{option.text}</span>
                      </div>
                    </motion.button>
                  ))}
                </div>

                {currentQuestion > 0 && (
                  <motion.button
                    onClick={() => {
                      setCurrentQuestion(currentQuestion - 1);
                      setSelectedOption(null);
                    }}
                    className="mt-8 flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                    whileHover={{ x: -5 }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Voltar
                  </motion.button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="results"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', bounce: 0.5, delay: 0.2 }}
                  className={`w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center relative ${
                    recommendation === 'member' 
                      ? 'bg-neon-green/10 border-2 border-neon-green/30' 
                      : 'bg-white/5 border-2 border-white/20'
                  }`}
                >
                  {recommendation === 'member' ? (
                    <>
                      <motion.div
                        className="absolute inset-0 rounded-full border-2 border-neon-green"
                        animate={{ scale: [1, 1.2, 1], opacity: [1, 0, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      />
                      <Crown className="w-12 h-12 text-neon-green" />
                    </>
                  ) : (
                    <Sparkles className="w-12 h-12 text-white/60" />
                  )}
                </motion.div>

                {recommendation === 'member' ? (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <span className="inline-block px-4 py-1.5 bg-neon-green/10 border border-neon-green/30 text-neon-green text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                        <Star className="w-3 h-3 inline mr-1" />
                        Recomendação
                      </span>
                      <h3 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-4">
                        Você é um <span className="gradient-text-green">Verdadeiro Membro</span>
                      </h3>
                      <p className="text-white/70 mb-8 max-w-xl mx-auto">
                        Você está pronto para construir, conectar e crescer.
                        O <span className="text-neon-green font-semibold">AI Brasil Membership Oficial</span> é o seu lugar.
                      </p>

                      <motion.a
                        href={membership.ctaUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-neon-green text-black font-black rounded-full text-lg relative overflow-hidden group shadow-[0_0_30px_rgba(0,255,0,0.3)]"
                        whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(0,255,0,0.5)' }}
                        whileTap={{ scale: 0.95 }}
                        data-testid="quiz-cta-member"
                      >
                        <motion.span
                          className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                          initial={{ x: '-100%' }}
                          whileHover={{ x: '100%' }}
                          transition={{ duration: 0.6 }}
                        />
                        <Crown className="w-5 h-5 relative z-10" />
                        <span className="relative z-10">Quero Ser Membro</span>
                        <ChevronRight className="w-5 h-5 relative z-10" />
                      </motion.a>
                    </motion.div>
                  </>
                ) : (
                  <>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.3 }}
                    >
                      <span className="inline-block px-4 py-1.5 bg-white/5 border border-white/20 text-white/60 text-xs font-bold uppercase tracking-wider rounded-full mb-4">
                        Recomendação
                      </span>
                      <h3 className="font-outfit font-black text-3xl sm:text-4xl text-white mb-4">
                        Comece explorando <span className="text-white/80">gratuitamente</span>
                      </h3>
                      <p className="text-white/60 mb-2 max-w-xl mx-auto">
                        Você pode entrar no ecossistema gratuitamente para conhecer.
                      </p>
                      <p className="text-neon-green text-sm mb-8 max-w-xl mx-auto font-semibold">
                        Mas se você quiser realmente construir, conectar e crescer, considere ser membro oficial.
                      </p>

                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <a
                          href={membership.freeCtaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-3 bg-neutral-800 text-neutral-300 border border-neutral-700 rounded-full font-medium hover:bg-neutral-700 transition-colors"
                          data-testid="quiz-cta-free"
                        >
                          Entrar gratuitamente
                        </a>
                        <motion.a
                          href={membership.ctaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-8 py-3 bg-neon-green text-black font-bold rounded-full"
                          whileHover={{ scale: 1.05 }}
                          data-testid="quiz-cta-upgrade"
                        >
                          <Crown className="w-4 h-4" />
                          Ver Membership
                        </motion.a>
                      </div>
                    </motion.div>
                  </>
                )}

                <motion.button
                  onClick={resetQuiz}
                  className="mt-8 text-white/40 hover:text-white text-sm transition-colors"
                  whileHover={{ scale: 1.05 }}
                >
                  Refazer Quiz
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default PlanRecommender;
